// frontend/src/pages/Customers/AddCustomer.js - UPDATED
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Typography,
  Divider,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Person as PersonIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { createCustomer } from '../../services/api';

const AddCustomer = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    customer_code: '',
    email: '',
    phone: '',
    company_name: '',
    address: '',
    customer_type: 'individual',
    is_active: true,
    
    // Credit Information
    credit_limit: '0',
    payment_terms: '30',
    is_credit_customer: false
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // If credit limit is set, automatically mark as credit customer
    if (name === 'credit_limit') {
      const creditValue = parseFloat(value) || 0;
      setFormData(prev => ({
        ...prev,
        [name]: value,
        is_credit_customer: creditValue > 0
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const generateCustomerCode = () => {
    const prefix = formData.customer_type === 'corporate' ? 'CORP' : 'INDV';
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `${prefix}-${timestamp}${random}`;
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError(null);
  
  try {
    // Validate required fields
    if (!formData.name.trim()) {
      throw new Error('Customer name is required');
    }
    
    // Prepare customer data for backend
    const customerData = {
      ...formData,
      credit_limit: parseFloat(formData.credit_limit) || 0,
      // IMPORTANT: Explicitly set is_credit_customer based on credit_limit
      is_credit_customer: parseFloat(formData.credit_limit) > 0,
      outstanding_balance: 0,
      // The backend will calculate available_credit automatically
      customer_code: formData.customer_code || '', // Let backend generate it
      is_active: true
    };

    const response = await createCustomer(customerData);
    
    if (response.success) {
      setSuccess(true);
      setTimeout(() => {
        onSuccess && onSuccess(response.data);
        handleClose();
      }, 1500);
    } else {
      setError(response.message || 'Failed to create customer');
    }
  } catch (err) {
    setError(err.message || 'An error occurred');
  } finally {
    setLoading(false);
  }
};

  const handleClose = () => {
    setFormData({
      name: '',
      customer_code: '',
      email: '',
      phone: '',
      company_name: '',
      address: '',
      customer_type: 'individual',
      credit_limit: '0',
      payment_terms: '30',
      is_credit_customer: false,
      is_active: true
    });
    setError(null);
    setSuccess(false);
    setActiveTab(0);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonIcon />
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
            Add New Customer
          </Typography>
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Customer created successfully!
            </Alert>
          )}

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Basic Information" />
              <Tab 
                label={(
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <CreditCardIcon fontSize="small" />
                    <span>Credit Details</span>
                  </Box>
                )} 
              />
            </Tabs>
          </Box>

          {activeTab === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Customer Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  margin="normal"
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Customer Code"
                  name="customer_code"
                  value={formData.customer_code}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                  placeholder="Leave blank to auto-generate"
                  helperText="Auto-generated if left blank"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Customer Type *</InputLabel>
                  <Select
                    name="customer_type"
                    value={formData.customer_type}
                    onChange={handleChange}
                    label="Customer Type *"
                  >
                    <MenuItem value="individual">Individual</MenuItem>
                    <MenuItem value="corporate">Corporate</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                  disabled={formData.customer_type === 'individual'}
                  helperText={formData.customer_type === 'individual' ? "Not applicable for individuals" : ""}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone *"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#e8f4fd', mb: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <CreditCardIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Credit Account Setup
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Set credit limit to enable credit purchases for this customer
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Credit Limit (LKR)"
                  name="credit_limit"
                  type="number"
                  value={formData.credit_limit}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                  InputProps={{ 
                    inputProps: { min: 0, step: 1000 },
                    startAdornment: 'LKR '
                  }}
                  helperText="Set to 0 for cash-only customer"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Payment Terms (days)"
                  name="payment_terms"
                  type="number"
                  value={formData.payment_terms}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                  InputProps={{ inputProps: { min: 0, max: 90 } }}
                  helperText="Default payment period"
                />
              </Grid>

              <Grid item xs={12}>
                <Alert 
                  severity={parseFloat(formData.credit_limit) > 0 ? "info" : "warning"}
                  sx={{ mt: 2 }}
                >
                  {parseFloat(formData.credit_limit) > 0 ? (
                    <>
                      <Typography variant="body2" fontWeight="bold">
                        Credit Account will be created
                      </Typography>
                      <Typography variant="caption" display="block">
                        • Credit Limit: LKR {parseFloat(formData.credit_limit).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" display="block">
                        • Available Credit: LKR {parseFloat(formData.credit_limit).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" display="block">
                        • Payment Terms: {formData.payment_terms} days
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" fontWeight="bold">
                        Cash-Only Customer
                      </Typography>
                      <Typography variant="caption">
                        This customer will not be able to make credit purchases.
                        Set a credit limit above 0 to enable credit features.
                      </Typography>
                    </>
                  )}
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_credit_customer"
                      checked={formData.is_credit_customer}
                      onChange={handleChange}
                      color="primary"
                      disabled={parseFloat(formData.credit_limit) <= 0}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        {formData.is_credit_customer ? "Credit Account Active" : "Credit Account Inactive"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {parseFloat(formData.credit_limit) <= 0 ? 
                          "Enable by setting credit limit above 0" : 
                          "Customer can make credit purchases"}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <FormControlLabel
                  control={
                    <Switch
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        {formData.is_active ? "Customer Active" : "Customer Inactive"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formData.is_active ? 
                          "Customer can make purchases" : 
                          "Customer cannot make purchases"}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleClose} 
            color="inherit"
            disabled={loading}
          >
            Cancel
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          {activeTab === 0 && (
            <Button 
              onClick={() => setActiveTab(1)}
              variant="outlined"
              disabled={loading || !formData.name}
            >
              Next: Credit Details
            </Button>
          )}
          {activeTab === 1 && (
            <Button 
              onClick={() => setActiveTab(0)}
              variant="outlined"
              disabled={loading}
            >
              Back to Basic Info
            </Button>
          )}
          <Button 
            type="submit" 
            variant="contained"
            disabled={loading || !formData.name}
            sx={{
              backgroundColor: '#3498db',
              '&:hover': { backgroundColor: '#2980b9' },
              ml: 1
            }}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? 'Creating...' : 'Create Customer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddCustomer;